import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { mergeBoardAPI } from '../../../services/api';
import { fetchWalletBalance } from '../../wallet/store/walletSlice';
import { fetchLootboxInventory } from '../../lootbox/store/lootboxSlice';
import { fetchNotifications } from '../../../store/slices/countersSlice';
import { enqueueReward } from '../../spin/store/rewardsQueueSlice';
import StatChips from '../../../shared/components/StatChips';
import Preloader from '../../../shared/components/Preloader';
import { perkText } from '../../../shared/utils/heroStats';
import { resolveAssetUrl } from '../../../shared/utils/assetUrl';
import {
  BOARD_SIZE, canMerge, cellMap, compactNumber, findMergePair, formatCountdown, fractionText,
  isBuyable, isFragment, isUnlocked, levelOf, looksAlike, mergeBlocker, msUntil, previewMerge,
} from '../utils/idleMergeMath';
import '../styles/mergeBoard.css';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { entityText, characterName } from '../../../i18n/entityText';
import { moduleTypeLabel, partName } from '../../../shared/utils/gameLabels';

// pointer travel (px) before a press becomes a drag
const DRAG_SLOP = 6;
// no interaction for this long → pulse the first mergeable pair
const IDLE_HINT_MS = 6000;
// 3 pulses of .mb-cell.is-hint
const IDLE_HINT_PLAY_MS = 2100;
// silent GET after a spawn may be retried this often if the server did not move `nextAt` yet
const SPAWN_RETRY_MS = 15000;

const haptic = (kind, style) => {
  const hf = window.Telegram?.WebApp?.HapticFeedback;
  if (!hf) return;
  if (kind === 'impact') hf.impactOccurred(style);
  else if (kind === 'select') hf.selectionChanged();
  else hf.notificationOccurred(style);
};

const errorText = (e) =>
  e?.response?.data?.message || e?.response?.data?.error || e?.message
  || i18n.t('common.something_wrong', 'Something went wrong');

const tt = (...args) => i18n.t(...args);

/** Fragment name (merge_fragment_configs) by its config id */
const fragmentName = (f) => entityText(tt, 'merge_fragment', f?.configId, 'name', f?.name) || '';

const FRAGMENT_FALLBACK_IMG = {
  TICKET: '/ticket_icon.png',
  CHEST: '/lootbox/icon_chest_green.png',
  KEY: '/lootbox/icon_key_green.png',
};

/** Image of a board piece: hero part or fragment */
const imageOf = (piece) => {
  if (!piece) return null;
  if (isFragment(piece)) return resolveAssetUrl(piece.imageUrl) || FRAGMENT_FALLBACK_IMG[piece.rewardType] || '/ticket_icon.png';
  return resolveAssetUrl(piece.module?.imageUrl);
};

const nameOf = (piece) => (isFragment(piece) ? fragmentName(piece) : partName(tt, piece?.module)) || '';

/** `claimed` of the claim response → payload of the shared reward popup (RewardCenter) */
function claimedToReward(c) {
  const value = Number(c.amount) || 1;
  const icon = resolveAssetUrl(c.imageUrl) || FRAGMENT_FALLBACK_IMG[c.rewardType];
  const entity = c.configId && c.name ? { prefix: 'merge_fragment', id: c.configId } : undefined;
  const base = { value, source: 'merge-board', icon, label: c.name || c.rewardType, entity };
  switch (c.rewardType) {
    case 'TICKET':
      return { ...base, type: 'TICKETS', label: c.name || 'Ticket', animationTarget: 'tickets', sound: 'gotTicket' };
    case 'CHEST':
      return { ...base, type: 'CHEST', chestImageUrl: icon, animationTarget: 'inventory', sound: 'reward' };
    case 'KEY':
      return { ...base, type: 'KEY', keyImageUrl: icon, animationTarget: 'inventory', sound: 'reward' };
    default:
      return { ...base, type: c.rewardType, animationTarget: 'inventory', sound: 'reward' };
  }
}

/** What a pointer is over: {x, y} of a cell, 'tray' (back to inventory) or null. */
function dropTargetAt(px, py) {
  const el = document.elementFromPoint?.(px, py);
  const cell = el?.closest?.('[data-cell]');
  if (cell) {
    const [x, y] = cell.getAttribute('data-cell').split(',').map(Number);
    return { x, y };
  }
  return el?.closest?.('[data-drop="tray"]') ? 'tray' : null;
}

/**
 * Merge Board 2.0 (v1.45.0): 7×7 board, the centre 3×3 is open, the rest is bought for coins (neighbours only).
 * Pieces: hero parts (from the inventory tray, back to it) and item fragments (spawn on a timer and in bought
 * cells; 1/4 + 1/4 → 2/4 … → whole item, tap to claim).
 * Moves are drag & drop only — a tap shows details; the only tap actions are "claim a whole item" and
 * "unlock a cell" (button in the panel).
 */
export default function MergeBoardTab({ inventory = [], onInventoryChanged }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [board, setBoard] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  // {kind: 'cell', x, y} | {kind: 'tray', id} | {kind: 'lock', x, y}
  const [selected, setSelected] = useState(null);
  // live drag: {source, px, py, over}
  const [drag, setDrag] = useState(null);
  const [flash, setFlash] = useState(null);
  // cells that just appeared (spawned fragments / bought cell): {keys: Set, n}
  const [appear, setAppear] = useState(null);
  // idle hint: {keys: [a, b], n}
  const [hint, setHint] = useState(null);
  const [idleTick, setIdleTick] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const press = useRef(null);
  const busyRef = useRef(false);
  const clockOffset = useRef(0);
  const spawnFetch = useRef({ nextAt: null, at: 0 });
  const hintShown = useRef(false);

  const cells = useMemo(() => cellMap(board?.cells), [board]);
  const rules = board?.rules;
  const maxLevel = rules?.maxLevel || Infinity;
  const width = board?.width || BOARD_SIZE;
  const trayItems = useMemo(() => inventory.filter((m) => m?.module && (m.quantity || 0) > 0), [inventory]);

  const isOpen = useCallback((x, y) => isUnlocked(board, x, y), [board]);

  const itemOf = useCallback((src) => {
    if (!src) return null;
    if (src.kind === 'cell') return cells[`${src.x},${src.y}`] || null;
    if (src.kind === 'tray') return trayItems.find((m) => m.id === src.id) || null;
    return null;
  }, [cells, trayItems]);

  /** Every board response goes through here: server clock offset, appear animation. */
  const applyBoard = useCallback((next, extraAppear = []) => {
    if (!next) return;
    const server = Date.parse(next.serverTime);
    if (!Number.isNaN(server)) clockOffset.current = server - Date.now();
    setNow(Date.now());
    setBoard(next);
    const keys = [...(next.spawned || []).map((c) => `${c.x},${c.y}`), ...extraAppear];
    if (keys.length) setAppear({ keys: new Set(keys), n: Date.now() });
  }, []);

  useEffect(() => {
    let alive = true;
    mergeBoardAPI.getBoard()
      .then((b) => alive && applyBoard(b))
      .catch((e) => alive && setLoadError(errorText(e)));
    return () => { alive = false; };
  }, [applyBoard]);

  // keep the selection valid after the board / inventory changed
  useEffect(() => {
    if (!selected) return;
    if (selected.kind === 'lock') {
      if (!isBuyable(board, selected.x, selected.y)) setSelected(null);
    } else if (!itemOf(selected)) {
      setSelected(null);
    }
  }, [selected, itemOf, board]);

  // soft hint in the panel, hides by itself
  const messageTimer = useRef(null);
  const softMessage = useCallback((text) => {
    setMessage(text);
    clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(null), 2200);
  }, []);
  useEffect(() => () => clearTimeout(messageTimer.current), []);

  // one-shot animation classes clean themselves up (so they can replay and don't fight other animations)
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 1200);
    return () => clearTimeout(t);
  }, [flash]);
  useEffect(() => {
    if (!appear) return undefined;
    const t = setTimeout(() => setAppear(null), 400);
    return () => clearTimeout(t);
  }, [appear]);

  const run = useCallback(async (call, { inventoryChanged = true, onDone, appearKeys } = {}) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setMessage(null);
    try {
      const next = await call();
      applyBoard(next, appearKeys);
      if (inventoryChanged) onInventoryChanged?.();
      onDone?.(next);
    } catch (e) {
      softMessage(errorText(e));
      haptic('notify', 'error');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [applyBoard, onInventoryChanged, softMessage]);

  // ── spawn timer: countdown on the server clock, silent GET when it hits 0 ──
  const spawn = board?.spawn;
  const nextAt = spawn?.nextAt || null;
  useEffect(() => {
    if (!nextAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [nextAt]);

  const spawnLeft = nextAt ? msUntil(nextAt, clockOffset.current, now) : null;
  const queueFull = !!spawn && spawn.maxPending != null && (spawn.pending || 0) >= spawn.maxPending;
  useEffect(() => {
    if (spawnLeft == null || spawnLeft > 0 || queueFull) return;
    if (busyRef.current || press.current?.dragging) return; // retried on the next tick
    const last = spawnFetch.current;
    if (last.nextAt === nextAt && now - last.at < SPAWN_RETRY_MS) return;
    spawnFetch.current = { nextAt, at: now };
    mergeBoardAPI.getBoard()
      .then((b) => { if (!busyRef.current) applyBoard(b); })
      .catch(() => {});
  }, [spawnLeft, queueFull, nextAt, now, applyBoard]);

  // ── idle hint: 6 s without touching → pulse the first mergeable pair (once per idle period) ──
  useEffect(() => {
    const onDown = () => {
      hintShown.current = false;
      setHint(null);
      setIdleTick((t) => t + 1);
    };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, []);

  useEffect(() => {
    if (!board || hintShown.current) return undefined;
    const t = setTimeout(() => {
      if (press.current || busyRef.current) return;
      const pair = findMergePair(cells, maxLevel, width);
      if (!pair) return;
      hintShown.current = true;
      setHint({ keys: pair, n: Date.now() });
    }, IDLE_HINT_MS);
    return () => clearTimeout(t);
  }, [board, cells, maxLevel, width, idleTick]);

  useEffect(() => {
    if (!hint) return undefined;
    const t = setTimeout(() => setHint(null), IDLE_HINT_PLAY_MS);
    return () => clearTimeout(t);
  }, [hint]);

  // ── actions ──────────────────────────────────────────────────────────
  const doMerge = (src, tx, ty) => {
    const sourceItem = itemOf(src);
    const source = src.kind === 'cell'
      ? { fromX: src.x, fromY: src.y }
      : { moduleId: sourceItem.module.id, rank: sourceItem.rank || 0 };
    run(() => mergeBoardAPI.merge(source, tx, ty), {
      inventoryChanged: src.kind === 'tray',
      onDone: (next) => {
        const m = next?.merged;
        setSelected(m?.fragment?.whole ? null : { kind: 'cell', x: tx, y: ty });
        const label = m?.fragment
          ? (m.fragment.whole ? i18n.t('merge.whole_pop', 'Whole!') : fractionText(m.fragment))
          : (m?.level ? i18n.t('lair.level_short', 'Lv {{n}}', { n: m.level }) : null);
        setFlash({ key: `${tx},${ty}`, label, same: m?.sameHero, n: Date.now() });
        haptic('impact', 'heavy');
        if (m?.coinsSpent > 0) dispatch(fetchWalletBalance());
      },
    });
  };

  const claim = (x, y) => {
    haptic('impact', 'medium');
    run(() => mergeBoardAPI.claim(x, y), {
      inventoryChanged: false,
      onDone: (next) => {
        setSelected(null);
        const c = next?.claimed;
        if (c) {
          dispatch(enqueueReward(claimedToReward(c)));
          softMessage(`+${c.amount || 1} ${entityText(tt, 'merge_fragment', c.configId, 'name', c.name) || ''}`.trim());
        }
        haptic('notify', 'success');
        dispatch(fetchWalletBalance());
        dispatch(fetchLootboxInventory());
        dispatch(fetchNotifications());
      },
    });
  };

  const buyCell = (x, y) => {
    run(() => mergeBoardAPI.buyCell(x, y), {
      inventoryChanged: false,
      appearKeys: [`${x},${y}`],
      onDone: () => {
        setSelected(null);
        haptic('notify', 'success');
        dispatch(fetchWalletBalance());
      },
    });
  };

  /** Drop resolution: place / move / swap / merge / take. */
  const act = (src, target) => {
    if (!src || !target) return;
    const sourceItem = itemOf(src);
    if (!sourceItem) return;
    const frag = isFragment(sourceItem);
    if (target === 'tray') {
      if (src.kind === 'cell') {
        if (frag) {
          softMessage(i18n.t('merge.fragments_stay', 'Fragments stay on the board'));
          haptic('notify', 'warning');
          return;
        }
        run(() => mergeBoardAPI.take(src.x, src.y));
        setSelected(null);
      }
      return;
    }
    const { x, y } = target;
    if (src.kind === 'cell' && src.x === x && src.y === y) return;
    if (!isOpen(x, y)) {
      softMessage(i18n.t('merge.cell_locked', 'This cell is locked'));
      haptic('notify', 'warning');
      return;
    }
    const targetItem = cells[`${x},${y}`];
    if (targetItem) {
      if (canMerge(sourceItem, targetItem, maxLevel)) {
        doMerge(src, x, y);
      } else if (src.kind === 'cell') {
        // not mergeable → the two pieces swap places; say why when it looked like a merge attempt
        run(() => mergeBoardAPI.move(src.x, src.y, x, y), { inventoryChanged: false });
        // after run(): it clears the panel message when it starts
        if (looksAlike(sourceItem, targetItem)) softMessage(mergeBlocker(sourceItem, targetItem, maxLevel));
        setSelected({ kind: 'cell', x, y });
      } else {
        softMessage(mergeBlocker(sourceItem, targetItem, maxLevel));
        haptic('notify', 'warning');
      }
      return;
    }
    if (src.kind === 'cell') {
      run(() => mergeBoardAPI.move(src.x, src.y, x, y), { inventoryChanged: false });
      setSelected({ kind: 'cell', x, y });
    } else {
      run(() => mergeBoardAPI.place(sourceItem.module.id, sourceItem.rank || 0, x, y));
      setSelected({ kind: 'cell', x, y });
    }
  };

  // Moves are drag & drop only. A tap shows details; a whole item is claimed; a buyable lock offers "Unlock".
  const tapCell = (x, y) => {
    if (!isOpen(x, y)) {
      if (isBuyable(board, x, y)) {
        haptic('select');
        setSelected(selected?.kind === 'lock' && selected.x === x && selected.y === y ? null : { kind: 'lock', x, y });
      } else {
        softMessage(i18n.t('merge.unlock_neighbours', 'Unlock neighbouring cells first'));
      }
      return;
    }
    const piece = cells[`${x},${y}`];
    if (!piece) return;
    if (isFragment(piece) && piece.whole) {
      claim(x, y);
      return;
    }
    haptic('select');
    setSelected(selected?.kind === 'cell' && selected.x === x && selected.y === y ? null : { kind: 'cell', x, y });
  };

  const tapTray = (item) => {
    if (!item) return;
    haptic('select');
    setSelected(selected?.kind === 'tray' && selected.id === item.id ? null : { kind: 'tray', id: item.id });
  };

  // ── drag with pointer events (mouse + touch) ─────────────────────────
  const onPointerDown = (e, src) => {
    if (busy || (e.button != null && e.button !== 0)) return;
    press.current = { src, sx: e.clientX, sy: e.clientY, dragging: false, pointerId: e.pointerId };
  };

  useEffect(() => {
    const move = (e) => {
      const p = press.current;
      if (!p || (p.pointerId != null && e.pointerId !== p.pointerId)) return;
      const dx = e.clientX - p.sx;
      const dy = e.clientY - p.sy;
      if (!p.dragging) {
        if (Math.hypot(dx, dy) < DRAG_SLOP) return;
        // locked cells can't be dragged; the tray scrolls horizontally, so only a mostly vertical pull lifts a part
        if (p.src.kind === 'locked' || (p.src.kind === 'tray' && Math.abs(dx) > Math.abs(dy))) {
          press.current = null;
          return;
        }
        p.dragging = true;
        haptic('impact', 'light');
      }
      e.preventDefault?.();
      setDrag({ source: p.src, px: e.clientX, py: e.clientY, over: dropTargetAt(e.clientX, e.clientY) });
    };
    const up = (e) => {
      const p = press.current;
      press.current = null;
      if (!p || (p.pointerId != null && e.pointerId !== p.pointerId)) return;
      if (p.dragging) {
        setDrag(null);
        act(p.src, dropTargetAt(e.clientX, e.clientY));
      } else if (e.type === 'pointerup') {
        if (p.src.kind === 'tray') tapTray(itemOf(p.src));
        else tapCell(p.src.x, p.src.y);
      }
    };
    const cancel = () => {
      press.current = null;
      setDrag(null);
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  });

  if (loadError && !board) {
    return (
      <div className="error-text">
        <div className="error-icon">⚠️</div>
        <span>{t('merge.load_failed', 'Failed to load the merge board')}</span>
      </div>
    );
  }
  if (!board) return <Preloader />;

  const size = width;
  const rows = board.height || BOARD_SIZE;
  const dragItem = drag ? itemOf(drag.source) : null;
  const overCell = drag?.over && drag.over !== 'tray' ? drag.over : null;
  const overOpen = overCell ? isOpen(overCell.x, overCell.y) : false;
  const overItem = overCell && overOpen ? cells[`${overCell.x},${overCell.y}`] : null;
  const selItem = itemOf(selected);
  const previewTarget = overItem && canMerge(dragItem, overItem, maxLevel) ? overItem : null;
  const preview = dragItem && previewTarget ? previewMerge(dragItem, previewTarget, rules) : null;
  const activeItem = dragItem || selItem;
  const total = size * rows;
  const openCount = Array.isArray(board.unlocked) ? board.unlocked.filter(Boolean).length : total;
  const filled = Object.keys(cells).length;
  const partsOnBoard = board.cells.some((c) => c.item);
  const dragFragment = dragItem && isFragment(dragItem);

  // Info panel under the board — fixed height, so the board never jumps while content changes
  let info;
  if (preview?.fragment) {
    info = (
      <div className="mb-preview" data-testid="merge-preview">
        <div className="mb-preview__title">
          {preview.whole
            ? t('merge.preview.whole', 'Merge → whole {{name}}', { name: entityText(t, 'merge_fragment', previewTarget.configId, 'name', preview.name) })
            : t('merge.preview.fraction', 'Merge → {{n}}/{{d}}', { n: preview.numerator, d: preview.denominator })}
        </div>
        <div className="mb-hint">
          {preview.whole
            ? t('merge.preview.free_claim', 'Free · tap the whole item to claim it')
            : t('merge.preview.free_keep', 'Free · keep merging equal pieces')}
        </div>
      </div>
    );
  } else if (preview) {
    info = (
      <div className="mb-preview" data-testid="merge-preview">
        <div className="mb-preview__title">
          {t('merge.preview.level', 'Merge → Lv {{n}}', { n: preview.level })}
          {preview.sameHero && <span className="mb-same">{t('merge.same_hero', 'Same hero +{{pct}}%', { pct: rules.sameHeroBonusPercent })}</span>}
          {preview.cost > 0 && <span className="mb-cost"><img src="/coin_icon.png" alt="" />{preview.cost}</span>}
        </div>
        <StatChips str={preview.str} agi={preview.agi} int={preview.int} />
      </div>
    );
  } else if (selected?.kind === 'lock') {
    info = (
      <div className="mb-detail mb-unlock">
        <img className="mb-detail__img mb-detail__img--lock" src="/lock.png" alt="" />
        <div className="mb-detail__body">
          <div className="mb-detail__title">{t('merge.unlock_title', 'Unlock this cell')}</div>
          <div className="mb-detail__sub">{t('merge.unlock_sub', 'More room to merge — may bring a free piece')}</div>
        </div>
        <button type="button" className="app-btn app-btn--green mb-btn mb-unlock__btn" disabled={busy}
          data-testid="unlock-btn" onClick={() => buyCell(selected.x, selected.y)}>
          {t('merge.unlock_btn', 'Unlock · {{price}}', { price: board.cellPrice })}<img src="/coin_icon.png" alt={t('common.coins', 'Coins')} />
        </button>
      </div>
    );
  } else if (activeItem && isFragment(activeItem)) {
    const next = activeItem.whole ? null : previewMerge(activeItem, activeItem, rules);
    info = (
      <div className="mb-detail">
        <img className="mb-detail__img" src={imageOf(activeItem)} alt={fragmentName(activeItem)} />
        <div className="mb-detail__body">
          <div className="mb-detail__title">
            <span className="mb-ellipsis">{fragmentName(activeItem)}</span>
            <span className="mb-lvl mb-lvl--frac">{activeItem.whole ? t('merge.whole', 'Whole') : fractionText(activeItem)}</span>
          </div>
          <div className="mb-detail__sub">
            {activeItem.whole
              ? t('merge.tap_to_claim', 'Tap to claim')
              : next.whole
                ? t('merge.two_pieces_whole', 'Merge two {{f}} pieces → whole item', { f: fractionText(activeItem) })
                : t('merge.two_pieces', 'Merge two {{f}} pieces → {{n}}/{{d}}', { f: fractionText(activeItem), n: next.numerator, d: next.denominator })}
          </div>
        </div>
      </div>
    );
  } else if (activeItem) {
    const perk = perkText(activeItem.perkType, activeItem.perkValue);
    info = (
      <div className="mb-detail">
        <img className="mb-detail__img" src={imageOf(activeItem)} alt={nameOf(activeItem)} />
        <div className="mb-detail__body">
          <div className="mb-detail__title">
            <span className="mb-ellipsis">{nameOf(activeItem)}</span>
            <span className="mb-lvl">{t('lair.level_short', 'Lv {{n}}', { n: levelOf(activeItem) })}</span>
          </div>
          <div className="mb-detail__sub mb-ellipsis">
            {moduleTypeLabel(t, activeItem.module.type)}
            {activeItem.character && ` · ${characterName(t, activeItem.character)}`}
            {perk && ` · ${perk}`}
          </div>
          <StatChips str={activeItem.str} agi={activeItem.agi} int={activeItem.int} />
        </div>
      </div>
    );
  } else {
    info = (
      <div className="mb-hint">
        <Trans
          i18nKey="merge.hint"
          defaults="Drag a part onto the <b>same body part of the same level</b> (any hero): 1+1 → 2. The part you drop <b>onto</b> stays, stats add up. Merge equal <b>pieces</b> (1/4+1/4 → 2/4), tap a whole item to claim. Tap a 🔒 to unlock a cell."
          components={{ b: <b /> }}
        />
      </div>
    );
  }

  let spawnText = null;
  if (spawn) {
    const waiting = t('merge.spawn.waiting', '{{n}}/{{max}} waiting', { n: spawn.pending || 0, max: spawn.maxPending ?? '?' });
    spawnText = queueFull
      ? t('merge.spawn.full', 'Drops queue full · {{waiting}} · free a cell', { waiting })
      : t('merge.spawn.next', 'Next drop in {{time}} · {{waiting}}', { time: formatCountdown(spawnLeft), waiting });
  }

  return (
    <div className="mb-root">
      <div className={`mb-board ${busy ? 'is-busy' : ''} ${drag ? 'is-dragging' : ''}`}
        style={{ '--mb-size': size }} data-testid="merge-board">
        {Array.from({ length: total }, (_, i) => {
          const x = i % size;
          const y = Math.floor(i / size);
          const key = `${x},${y}`;
          const open = isOpen(x, y);
          const buyable = !open && isBuyable(board, x, y);
          const item = open ? cells[key] : null;
          const frag = isFragment(item);
          const isSource = drag?.source.kind === 'cell' && drag.source.x === x && drag.source.y === y;
          const isOver = overCell && overCell.x === x && overCell.y === y;
          const isSel = !drag && (selected?.kind === 'cell' || selected?.kind === 'lock')
            && selected.x === x && selected.y === y;
          const compatible = activeItem && item && !isSel && !isSource && canMerge(activeItem, item, maxLevel);
          let overCls = '';
          if (isOver) {
            if (!open) overCls = 'is-over is-over--locked';
            else if (item && !isSource) overCls = compatible ? 'is-over is-over--merge' : 'is-over is-over--swap';
            else overCls = 'is-over';
          }
          const cls = [
            'mb-cell',
            open ? '' : 'is-locked',
            buyable ? 'is-buyable' : '',
            item ? 'has-item' : '',
            frag ? 'is-fragment' : '',
            frag && item.whole ? 'is-whole' : '',
            isSource ? 'is-source' : '',
            overCls,
            isSel ? 'is-selected' : '',
            compatible ? 'is-compatible' : '',
            compatible && drag ? 'is-swing' : '',
            activeItem && item && !compatible && !isSel && !isSource ? 'is-dim' : '',
            flash?.key === key ? 'is-merged' : '',
            appear?.keys.has(key) ? 'is-spawned' : '',
            hint?.keys.includes(key) ? 'is-hint' : '',
          ].filter(Boolean).join(' ');
          let onDown;
          if (!open) onDown = (e) => onPointerDown(e, { kind: 'locked', x, y });
          else if (item) onDown = (e) => onPointerDown(e, { kind: 'cell', x, y });
          return (
            <div
              key={[key, flash?.key === key && flash.n, appear?.keys.has(key) && appear.n, hint?.keys.includes(key) && hint.n]
                .filter(Boolean).join(':')}
              className={cls}
              data-cell={key}
              data-testid={`cell-${key}`}
              onPointerDown={onDown}
            >
              {!open && (
                <>
                  <img className="mb-cell__lock" src="/lock.png" alt="locked" draggable={false} />
                  {buyable && board.cellPrice != null && (
                    <span className="mb-cell__price">
                      <img src="/coin_icon.png" alt="" draggable={false} />{compactNumber(board.cellPrice)}
                    </span>
                  )}
                </>
              )}
              {item && !isSource && (
                <>
                  <img className="mb-cell__img" src={imageOf(item)} alt={nameOf(item)} draggable={false} />
                  {frag ? (
                    item.whole
                      ? <span className="mb-cell__claim">{t('common.claim', 'Claim')}</span>
                      : <span className={`mb-cell__frac mb-cell__lvl--${Math.min(levelOf(item), 6)}`}>{fractionText(item)}</span>
                  ) : (
                    <span className={`mb-cell__lvl mb-cell__lvl--${Math.min(levelOf(item), 6)}`}>{levelOf(item)}</span>
                  )}
                  {!frag && item.rank > 0 && <span className="mb-cell__rank" style={{ background: item.rankColor }} />}
                </>
              )}
              {/* the dragged piece snaps into the (open) cell under the finger */}
              {isOver && open && dragItem && (
                <img className="mb-cell__drag" src={imageOf(dragItem)} alt="" draggable={false} />
              )}
              {flash?.key === key && flash.label && (
                <span className={`mb-pop ${flash.same ? 'mb-pop--same' : ''}`}>{flash.label}</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="mb-spawn" data-testid="merge-spawn">
        {spawnText && <><span className="mb-spawn__icon" aria-hidden="true">⏱</span>{spawnText}</>}
      </div>

      <div className="mb-panel" aria-live="polite" data-testid="merge-panel">
        {info}
        {message && <div className="mb-message" role="alert">{message}</div>}
      </div>

      <div className="mb-actions">
        <span className="mb-count">{filled}/{openCount}</span>
        <button type="button" className="app-btn app-btn--purple mb-btn" disabled={busy || trayItems.length === 0}
          onClick={() => run(() => mergeBoardAPI.fill())}>
          {t('merge.fill', 'Fill board')}
        </button>
        <button type="button" className="app-btn app-btn--blue mb-btn" disabled={busy || !partsOnBoard}
          onClick={() => { setSelected(null); run(() => mergeBoardAPI.collect()); }}>
          {t('merge.to_inventory', 'To inventory')}
        </button>
      </div>

      <div className={`mb-tray ${drag?.over === 'tray' ? 'is-over' : ''} ${drag?.source.kind === 'cell' ? 'is-drop' : ''} ${dragFragment ? 'is-blocked' : ''}`}
        data-drop="tray" data-testid="merge-tray">
        {drag?.source.kind === 'cell' && (
          <div className="mb-tray__drop">
            {dragFragment ? t('merge.fragments_stay', 'Fragments stay on the board') : `↩ ${t('merge.drop_return', 'Drop here to return to inventory')}`}
          </div>
        )}
        {trayItems.length === 0 ? (
          <div className="mb-tray__empty">{t('merge.tray_empty', 'Inventory is empty — parts drop in Lucky Spin, chests and missions')}</div>
        ) : (
          trayItems.map((m) => {
            const isSel = selected?.kind === 'tray' && selected.id === m.id;
            const compatible = activeItem && !isSel && canMerge(activeItem, m, maxLevel);
            return (
              <div
                key={m.id}
                className={`mb-tray__item ${isSel ? 'is-selected' : ''} ${compatible ? 'is-compatible' : ''}`}
                data-testid={`tray-${m.id}`}
                onPointerDown={(e) => onPointerDown(e, { kind: 'tray', id: m.id })}
              >
                <img src={resolveAssetUrl(m.module.imageUrl)} alt={partName(t, m.module)} draggable={false} />
                <span className={`mb-cell__lvl mb-cell__lvl--${Math.min(levelOf(m), 6)}`}>{levelOf(m)}</span>
                {m.quantity > 1 && <span className="mb-tray__qty">{m.quantity}</span>}
              </div>
            );
          })
        )}
      </div>

      {/* off the board (e.g. over the tray) the piece follows the finger */}
      {drag && dragItem && !(overCell && overOpen) && (
        <img
          className="mb-ghost"
          data-testid="merge-ghost"
          src={imageOf(dragItem)}
          alt=""
          style={{ left: drag.px, top: drag.py }}
        />
      )}
    </div>
  );
}
